import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Camera,
  Scissors,
  Eye,
  Sparkles,
  Droplet,
  Check,
  RotateCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api from "@/lib/api";
import { budgetBandLabel } from "@/lib/budget";
import { useToast } from "@/components/Toast";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";

async function fileToResizedDataUrl(file, maxDim = 1200) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const s = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * s); height = Math.round(height * s);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.86));
      };
      img.onerror = () => reject(new Error("decode"));
      img.src = r.result;
    };
    r.onerror = () => reject(new Error("read"));
    r.readAsDataURL(file);
  });
}

function PickList({ title, icon: Icon, items, testIdPrefix }) {
  if (!items?.length) return null;
  return (
    <div className="outfit-section" data-testid={`${testIdPrefix}-section`}>
      <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <Icon size={12} strokeWidth={1.8}/> {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it, i) => (
          <div key={i} className="card" style={{ padding: 14 }} data-testid={`${testIdPrefix}-${i}`}>
            <div className="h3" style={{ color: "#fff" }}>{it.name}</div>
            <div className="body-sm" style={{ color: "rgba(255,255,255,0.65)", marginTop: 6 }}>
              {it.why}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ColorChip() { return null; } // legacy — color recommendations removed from glow analysis

export default function GlowUpAnalyzeScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [error, setError] = useState(null);     // { type, retryable, message, request_id }
  const [lastB64, setLastB64] = useState(null); // for Try Again
  const [showMore, setShowMore] = useState(false); // hair/beard/glasses behind a toggle

  useEffect(() => {
    // Spec: face/skin/hair/beard/glasses/color recommendations should NOT appear
    // until a real photo is uploaded in THIS session. We intentionally do NOT
    // hydrate cached analysis on mount. (Cached results are still retrievable
    // from the backend if a future "Previous analyses" UI is added.)
  }, []);

  const runAnalysis = async (b64) => {
    setError(null);
    setLoading(true);
    setAnalysis(null);
    try {
      const { data } = await api.post(
        "/glow-up/analyze",
        { image_base64: b64 },
        { timeout: 90_000 } // give Vision room — backend already retries up to 3x
      );
      setAnalysis(data);
      toast.show("Analysis ready", "success");
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally { setLoading(false); }
  };

  const onPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError(null);
    try {
      const dataUrl = await fileToResizedDataUrl(f, 1200);
      setPreviewImg(dataUrl);
      const b64 = dataUrl.split(",", 2)[1];
      setLastB64(b64);
      await runAnalysis(b64);
    } catch (err) {
      setError({
        type: "upload_failure",
        retryable: false,
        message: "Couldn't read that file. Try a JPEG or PNG under 6 MB.",
      });
      setLoading(false);
    }
  };

  const onRetry = () => { if (lastB64) runAnalysis(lastB64); };
  const onPickNew = () => fileRef.current?.click();

  const face = analysis?.face;
  const skin = analysis?.skin;

  return (
    <div className="screen with-nav fade-in" data-testid="glow-analyze-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/progress")} aria-label="Back" data-testid="glow-analyze-back">
          <ChevronLeft size={18}/>
        </button>
        <div className="logo-mark">GLOW ANALYSIS</div>
        <span className="premium-pill">FREE</span>
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>NOVA · SKIN ANALYSIS</div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 18, lineHeight: 1.12 }}>
        Your skin first.<br/>
        <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Then the rest.</em>
      </h2>

      {!analysis && !loading && (
        <div className="photo-empty" data-testid="glow-analyze-cta" style={{ marginTop: 8 }}>
          <div className="photo-empty-icon"><Camera size={18} strokeWidth={1.8}/></div>
          <div className="body" style={{ color: "#fff", marginBottom: 6 }}>Upload a front-facing selfie</div>
          <div className="tiny" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.10em" }}>
            Soft daylight · no sunglasses · centered face
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn btn-gold" onClick={() => camRef.current?.click()} data-testid="glow-analyze-camera">
              <Camera size={14}/> Take selfie
            </button>
            <button className="btn btn-outline" onClick={() => fileRef.current?.click()} data-testid="glow-analyze-gallery">
              Choose
            </button>
            <input ref={camRef} type="file" accept="image/jpeg,image/png" capture="user" hidden onChange={onPicked} data-testid="glow-analyze-cam-input"/>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={onPicked} data-testid="glow-analyze-file-input"/>
          </div>
        </div>
      )}

      {error && !loading && (
        <AnalyzerErrorCard
          error={error}
          onRetry={lastB64 ? onRetry : undefined}
          onPickNew={onPickNew}
          feature="glow-up-analyze"
          testId="glow-analyze-error"
        />
      )}

      {loading && (
        <AnalyzerProgress previewImg={previewImg} totalSeconds={35} testId="glow-analyze-loading" />
      )}

      {analysis && (
        <>
          {/* SKINCARE — leads the analysis. Real, honest assessment. */}
          {skin && (
            <div
              className="glow-empty-hero"
              data-testid="glow-analyze-skin"
              style={{ marginTop: 8, borderColor: "rgba(180,220,255,0.18)" }}
            >
              <div className="glow-empty-badge"><Droplet size={14} strokeWidth={2}/> Skin observations</div>
              <div style={{ marginTop: 14 }}>
                <h3 className="h2" style={{ fontSize: 26, color: "var(--text)", textTransform: "capitalize" }}>
                  {skin.type ? `${skin.type} skin` : "Your skin"}
                </h3>
              </div>
              {skin.condition_summary && (
                <p className="body-sm" data-testid="glow-skin-summary" style={{ marginTop: 8, color: "rgba(255,255,255,0.78)" }}>
                  {skin.condition_summary}
                </p>
              )}
              {skin.whats_working && (
                <div className="card" data-testid="glow-skin-working" style={{ marginTop: 12, padding: 14, borderColor: "rgba(123,224,150,0.32)", background: "rgba(123,224,150,0.08)" }}>
                  <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "#7be096" }}>
                    WHAT&apos;S ALREADY WORKING
                  </div>
                  <div className="body" style={{ marginTop: 4, color: "var(--text)" }}>{skin.whats_working}</div>
                </div>
              )}
              {skin.observations?.length > 0 && (
                <div style={{ marginTop: 14 }} data-testid="glow-skin-observations">
                  <div className="eyebrow" style={{ marginBottom: 8 }}>WHAT WE NOTICED</div>
                  <ul className="bullet-list" style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
                    {skin.observations.map((o, i) => (
                      <li key={i} className="body-sm" data-testid={`glow-skin-obs-${i}`} style={{ color: "var(--text-dim)", padding: "5px 0", lineHeight: 1.55 }}>
                        <span style={{ color: "var(--gold)", marginRight: 8 }}>•</span>{o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {skin.concerns?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }} data-testid="glow-skin-concerns">
                  {skin.concerns.map((c, i) => (
                    <span key={i} className="chip" style={{ pointerEvents: "none" }}>{c}</span>
                  ))}
                </div>
              )}
              {skin.why_it_happens && (
                <div style={{ marginTop: 14 }} data-testid="glow-skin-why">
                  <div className="eyebrow" style={{ marginBottom: 6 }}>WHY IT HAPPENS</div>
                  <div className="body-sm" style={{ color: "var(--text-dim)", lineHeight: 1.55 }}>{skin.why_it_happens}</div>
                </div>
              )}
              {skin.priority_routine_step && (
                <div
                  className="card"
                  data-testid="glow-skin-priority"
                  style={{
                    marginTop: 14,
                    padding: 14,
                    border: "1px solid var(--gold-line)",
                    background: "var(--gold-soft)",
                  }}
                >
                  <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--gold)" }}>
                    PRIORITY MOVE
                  </div>
                  <div className="body" style={{ marginTop: 4, color: "var(--text)" }}>
                    {skin.priority_routine_step}
                  </div>
                </div>
              )}
              {skin.expected_timeline && (
                <div className="tiny" data-testid="glow-skin-timeline" style={{ marginTop: 12, color: "var(--text-faint)" }}>
                  <strong style={{ color: "var(--text-dim)", letterSpacing: "0.06em" }}>Expected timeline:</strong> {skin.expected_timeline}
                </div>
              )}
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate("/improve/skincare")}
                data-testid="glow-skin-cta"
                style={{ marginTop: 14 }}
              >
                <Sparkles size={14}/> Build my skincare routine
              </button>
            </div>
          )}

          {/* FACE shape — REMOVED per user request 2026-06-25: face shape is
              already shown in the dedicated profile flow; repeating it here
              clutters the improvement plan. Skin + product picks remain. */}

          {/* Launch spec (2026-02-26): "See hair, beard & glasses" removed.
              This page is skin-only. Other grooming details belong elsewhere. */}

          {/* GROOMING TIPS — REMOVED per user request 2026-06-25: these belong
              inside the Grooming Coach, not on the improvement-plan page. */}

          {/* Skincare product recommendations — affordable, real drugstore brands */}
          {analysis.skincare_products && Object.keys(analysis.skincare_products).length > 0 && (
            <div className="outfit-section" data-testid="glow-skincare-products">
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Droplet size={12} strokeWidth={1.8}/> RECOMMENDED PRODUCTS
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  ["cleanser",    "Cleanser"],
                  ["treatment",   "Treatment"],
                  ["moisturizer", "Moisturizer"],
                  ["sunscreen",   "Sunscreen"],
                  ["weekly_extra","Weekly extra"],
                ].map(([key, label]) => {
                  const p = analysis.skincare_products[key];
                  if (!p || !p.name) return null;
                  return (
                    <div key={key} className="card" data-testid={`glow-product-${key}`} style={{ padding: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                        <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--gold)" }}>
                          {label}{p.frequency ? ` · ${p.frequency}` : ""}
                        </div>
                        {(p.budget_band || p.price_band) && (
                          <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.14em", textTransform: "uppercase" }}>{budgetBandLabel(p.budget_band || p.price_band)}</div>
                        )}
                      </div>
                      <div className="body" style={{ marginTop: 6, color: "var(--text)" }}>
                        <strong>{p.brand ? `${p.brand} — ` : ""}</strong>{p.name}
                      </div>
                      {p.why && (
                        <div className="body-sm" style={{ marginTop: 4, color: "var(--text-faint)" }}>{p.why}</div>
                      )}
                      {p.how_to_apply && (
                        <div
                          className="how-to-apply"
                          data-testid={`glow-product-${key}-howto`}
                        >
                          <div className="how-to-apply-label">HOW TO APPLY</div>
                          <div className="how-to-apply-body">{p.how_to_apply}</div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => {
                          const q = `${p.brand || ""} ${p.name || ""}`.trim();
                          window.open(
                            `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(q)}`,
                            "_blank", "noopener,noreferrer"
                          );
                        }}
                        data-testid={`glow-product-${key}-shop`}
                        style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                      >
                        Find it
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="tiny" style={{ marginTop: 10, color: "var(--text-faint)", textAlign: "center" }}>
                Always patch-test new products. Adjust to your skin&apos;s reaction.
              </div>
            </div>
          )}

          {/* Daily routine — AM + PM steps */}
          {analysis.routine && (analysis.routine.morning?.length || analysis.routine.night?.length) > 0 && (
            <div className="outfit-section" data-testid="glow-routine">
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Droplet size={12} strokeWidth={1.8} /> YOUR DAILY ROUTINE
              </div>
              {analysis.routine.morning?.length > 0 && (
                <div className="routine-block" data-testid="glow-routine-morning">
                  <div className="routine-block-header"><span className="routine-emoji">☀️</span> MORNING</div>
                  <ol className="routine-steps">
                    {analysis.routine.morning.map((s, i) => (
                      <li key={i} className="routine-step" data-testid={`glow-routine-morning-${i}`}>
                        <div className="routine-step-num">{s.step ?? (i + 1)}</div>
                        <div>
                          <div className="routine-step-product">{s.product}</div>
                          {s.instruction && <div className="routine-step-instruction">{s.instruction}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {analysis.routine.night?.length > 0 && (
                <div className="routine-block" data-testid="glow-routine-night">
                  <div className="routine-block-header"><span className="routine-emoji">🌙</span> NIGHT</div>
                  <ol className="routine-steps">
                    {analysis.routine.night.map((s, i) => (
                      <li key={i} className="routine-step" data-testid={`glow-routine-night-${i}`}>
                        <div className="routine-step-num">{s.step ?? (i + 1)}</div>
                        <div>
                          <div className="routine-step-product">{s.product}</div>
                          {s.instruction && <div className="routine-step-instruction">{s.instruction}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn btn-outline"
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            data-testid="glow-analyze-redo"
            style={{ marginTop: 18 }}
          >
            <RotateCw size={14}/> Re-analyze with a new photo
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={onPicked}/>
        </>
      )}
    </div>
  );
}
